import { bunja } from 'bunja';
import { atom } from 'jotai';

import { activeVouchersBunja } from '@flipster/features/src/reward-hub-page/v2/vouchers/state';
import {
  getMarketDiscountState,
  type FeeDiscountState,
} from '@flipster/features/src/shared/fee-discount/market-discount';
import { FormattedMessage } from '@flipster/i18n';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

import { symbolBunja } from './base';
import { convert, formBunja, getKindValue } from './form';

const getTriggerDiscountState = (voucher: BigNumber): FeeDiscountState | null => {
  if (voucher.isZero()) return null;
  return {
    displayFeeRatePercent: null,
    message: <FormattedMessage id='Preview.r260128.Feediscount_voucher_trigger' />,
  };
};

const resolveFeeDiscountState = (
  estFee: BigNumber,
  priceType: 'trigger' | 'market' | 'limit',
  voucher: BigNumber,
): FeeDiscountState | null => {
  switch (priceType) {
    case 'trigger':
      return getTriggerDiscountState(voucher);
    case 'market':
      return getMarketDiscountState(estFee, voucher);
    default:
      return null;
  }
};

export const tradingFeeBunja = bunja(() => {
  const { symbol, symbolInfoAtom } = bunja.use(symbolBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { orderStateAtom, calculationPriceAtom, priceTypeAtom } = bunja.use(formBunja);
  const { perpFeeVoucherAvailableAmountAtom } = bunja.use(activeVouchersBunja);

  const takerFeeRateAtom = atom((get) => {
    const pswaps = get(pswapsAtom);
    return pswaps?.[symbol]?.takerFeeRate ?? '0';
  });

  const tradingFeeRateAtom = atom((get) => {
    const pswaps = get(pswapsAtom);
    const pswap = pswaps?.[symbol];
    const taker = Num(pswap?.takerFeeRate ?? '0');
    const maker = Num(pswap?.makerFeeRate ?? '0');
    if (maker.isZero()) return taker.toString();
    return BigNumber.max(taker, maker).toString();
  });

  const estimatedTradingFeeRatePercentAtom = atom((get) => {
    const tradingFeeRate = get(tradingFeeRateAtom);
    return Num(tradingFeeRate).multipliedBy(100).toString();
  });

  const convertedOrderStateAtom = atom((get) => {
    const orderState = get(orderStateAtom);
    const calculationPrice = get(calculationPriceAtom);
    const symbolInfo = get(symbolInfoAtom);
    const quantityPrecision = symbolInfo?.tradeQtyPrecision ?? 2;
    const price = calculationPrice ?? '0';
    return convert(orderState.leverage, price, { quantityPrecision })[orderState.kind](getKindValue(orderState));
  });

  const estimatedTradingFeeAtom = atom((get) => {
    const convertedState = get(convertedOrderStateAtom);
    const tradingFeeRate = get(tradingFeeRateAtom);
    const positionSizeInUsdt = convertedState.amount;
    return Num(positionSizeInUsdt).multipliedBy(tradingFeeRate).toFixed(UsdtPrecision, BigNumber.ROUND_UP);
  });

  const feeDiscountStateAtom = atom((get) => {
    const estTradingFee = Num(get(estimatedTradingFeeAtom));
    const priceType = get(priceTypeAtom);
    const voucher = get(perpFeeVoucherAvailableAmountAtom);
    return resolveFeeDiscountState(estTradingFee, priceType, voucher);
  });

  const isFullDiscountAtom = atom((get) => {
    const state = get(feeDiscountStateAtom);
    return state?.displayFeeRatePercent && state.displayFeeRatePercent.isZero();
  });

  return {
    takerFeeRateAtom,
    tradingFeeRateAtom,
    estimatedTradingFeeRatePercentAtom,
    convertedOrderStateAtom,
    estimatedTradingFeeAtom,
    feeDiscountStateAtom,
    isFullDiscountAtom,
  };
});

tradingFeeBunja.debugLabel = 'tradingFeeBunja';
